import React, { useState, useEffect } from 'react';
import { Table, notification } from "antd";
import { ColumnsType } from 'antd/es/table';
import { useSelector } from 'react-redux';
import { RootState } from '../../shared/redux/store';
import API_SERVICE from '../../shared/services/api-service';
import './index.scss';
import { getUser, fileUploadCommon, downloadDocument } from 'shared/services/Utility';
import AppUpload from 'shared/components/Lead/AppUpload/app-upload';
import { ReactComponent as PdfUpload } from '../../assets/images/pdf_upload.svg';
import { ReactComponent as PdfDownload } from '../../assets/images/pdf_download.svg';

interface DataType {
  key: string;
  id: string; // Invoice ID used for API calls
  invoiceNo: string;
  countOfDisbursal: number;
  invoiceDate: string;
  lenderName: string;
  documentSetId?: string; // Optional documentSetId for document uploads
  lenderInvoiceDocumentId?: string | null; // Add this property for document association
}

interface Props {
  filterValues: any;
}

const ReconciliationInvoices: React.FC<Props> = ({ filterValues }) => {
  const { appColors }: any = useSelector((state: RootState) => state.common);
  const { searchParam } = useSelector((state: RootState) => state.reconciliation);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [pageNo, setPageNo] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [data, setData] = useState<DataType[]>([]);
  const tenantId = getUser()?.businessId;

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNo, pageSize, filterValues, searchParam]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const filteredValuesWithTenantId = {
        ...filterValues,
        tenantIds: [tenantId],
        searchParam // Include searchParam in the body payload for invoices tab
      }
      const payload = {
        pageNo,
        pageSize,
        ...filteredValuesWithTenantId
      };
      
      const response = await API_SERVICE.getReconcileLenderInvoices(payload);
      const fetchedData = response?.data?.payload?.content || [];

        setData(fetchedData.map((item: any, index: number) => ({
          ...item,
          id: item.id, 
          documentSetId: item.documentSetId,
          key: item.invoiceNo || index.toString()
        })));
        setTotalCount(response?.data?.totalElements || 0);
      
    } catch (error) {
      notification.error({ message: API_SERVICE.handleErrors(error) });
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePaginationChange = (page: number, size: number = pageSize) => {
    setPageNo(page);
    if (size !== pageSize) {
      setPageSize(size);
      // Reset to page 1 when changing page size
      setPageNo(1);
    }
  };

  const handleUploadPdf = async (record: DataType, file: any) => {
    if (!file) {
      return;
    }

    setLoading(true);
    
    try {
      const uploadResponse = await fileUploadCommon(file);
      
      if (!uploadResponse.data) {
        notification.error({ message: "Failed to upload file. Please try again." });
        setLoading(false);
        return;
      }
      
      const documentURL = uploadResponse.data;
      const documentSetId = record.documentSetId || "";

      const payload = {
        documentURL: documentURL,
        documentName: file.name || 'invoice.pdf',
        documentType: 'INVOICE_DOCUMENT',
        documentSubType: 'LENDER_INVOICE_DOCUMENT',
      };

      const documentResponse = await API_SERVICE.addDocument(payload, documentSetId);
      
      if (!documentResponse || !documentResponse.data || !documentResponse.data.payload || !documentResponse.data.payload.businessDocumentId) {
        notification.error({ message: "Failed to process document. Please try again." });
        setLoading(false);
        return;
      }
      
      const docResponse = documentResponse.data.payload;
      await addInvoiceDocument(
        record.id, 
        docResponse.documentSetId, 
        docResponse.businessDocumentId
      );
      
      notification.success({ message: "Document uploaded successfully" });
      setPageNo(1); // Reset to first page after successful import
      fetchInvoices(); // Refresh the list
      
    } catch (error) {
      notification.error({ message: API_SERVICE.handleErrors(error) });
    } finally {
      setLoading(false);
    }
  };

  const addInvoiceDocument = async (invoiceId: string, documentSetId: string, documentId: string) => {
    try {
      const payload = {
        invoiceId,
        documentSetId,
        documentId
      };
      
      // Use the addInvoiceDocument API function
      const response = await API_SERVICE.addInvoiceDocument(payload);
      
      
      if (!response || !response.data) {
        throw new Error('Failed to associate document with invoice');
      }
      return true;
    } catch (error) {
      notification.error({ message: API_SERVICE.handleErrors(error) });
      return false;
    }
  };

  const columns: ColumnsType<DataType> = [
    {
      title: 'Invoice No.',
      dataIndex: 'invoiceNo',
      key: 'invoiceNo',
    },
    {
      title: 'No. of disbursals',
      dataIndex: 'countOfDisbursal',
      key: 'countOfDisbursal',
      align: 'center',
    },
    {
      title: 'Invoice date',
      dataIndex: 'invoiceDate',
      key: 'invoiceDate',
    },
    {
      title: 'Lender',
      dataIndex: 'lenderName',
      key: 'lenderName',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record: any) => (
        <div className="pdf-actions">
          <div className={`pdf-action-item${(record.lenderInvoiceDocumentId || record.invoiceSummary?.lenderInvoiceDocumentId) ? ' pdf-action-item-disabled' : ''}`}>
            <AppUpload
              acceptType={['pdf']}
              className="upload-wrapper"
              showUploadList={false}
              setUploadedFile={() => {}}
              setFileSize={() => {}}
              setIsSelectedLenderDoc={() => {}}
              callBack={(response) => handleUploadPdf(record, response?.file)}
              disabled={!!(record.lenderInvoiceDocumentId || record.invoiceSummary?.lenderInvoiceDocumentId)}
            >
              <PdfUpload height={25} width={25}/>
            </AppUpload>
          </div>
          <div
            className={`pdf-action-item${record?.invoiceSummary?.lenderInvoiceDocumentId ? '' : ' pdf-action-item-disabled'}`}
            onClick={record?.invoiceSummary?.lenderInvoiceDocumentId ? () => downloadDocument({businessDocumentId: record?.invoiceSummary?.lenderInvoiceDocumentId}) : undefined}
          >
            <PdfDownload height={25} width={25}/>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="reconciliation-content-box">
      <div className="invoices-table-container">
        <Table
          rowKey="key"
          className="dynamic-pagination-checkbox table-full-width reconciliation-tight-table reconciliation-table-with-footer"
          style={{ '--bgColor': appColors?.appPrimaryColor ?? '' } as React.CSSProperties}
          columns={columns}
          dataSource={data}
          loading={loading}
          size="small"
          scroll={{ x: 'max-content' }}
          pagination={{
            current: pageNo,
            pageSize: pageSize,
            total: totalCount,
            showSizeChanger: true,
            showQuickJumper: false,
            position: ['bottomRight'],
            onChange: handlePaginationChange,
            onShowSizeChange: handlePaginationChange,
          }}
        />
      </div>
    </div>
  );
};

export default ReconciliationInvoices;
